import { useEffect } from 'react';
import { useLocation, Navigate, useNavigate } from 'react-router-dom';
import DesktopSplit from '../components/DesktopSplit';
import { useFlow } from '../state/flow';
import { FormLeft, FormRight } from './IDCardForm';
import { PhotoLeft, PhotoRight } from './Photo';
import './FormPhotoFlow.scss';

/**
 * Layout that wraps /id-card and /photo so DesktopSplit's DOM survives the
 * navigation. CSS transitions on flex-grow handle the 60/40 → 40/60 slide,
 * and opacity transitions on stacked layers handle the content crossfade.
 * Both pages' content is mounted at all times — the inactive layer sits at
 * opacity 0 with `pointer-events: none`.
 */
export default function FormPhotoFlow() {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasFormData, setPhoto, setCardImage, setShareImages } = useFlow();
  const isPhoto = location.pathname === '/photo';

  // /share's "Start Over" sends the user here with `state.resetPhoto` set so
  // the photo + derived card/share images are cleared without flipping
  // `hasPhoto` while still on /share (which would trip the WelcomeShareFlow
  // guard and bounce through the home preloader).
  useEffect(() => {
    const state = location.state as { resetPhoto?: boolean } | null;
    if (!state?.resetPhoto) return;
    setPhoto(null);
    setCardImage(null);
    setShareImages(null);
    // Strip the marker so a refresh or back/forward doesn't re-clear state.
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate, setPhoto, setCardImage, setShareImages]);

  if (isPhoto && !hasFormData) {
    return <Navigate to="/" replace />;
  }

  return (
    <DesktopSplit
      desktopRatio={isPhoto ? '40-60' : '60-40'}
      mobileHide={isPhoto ? 'left' : 'right'}
      left={
        <div className="flow-pane">
          <div
            className={`flow-pane__layer flow-pane__layer--form${
              !isPhoto ? ' is-active' : ''
            }`}
            aria-hidden={isPhoto}
          >
            <FormLeft />
          </div>
          <div
            className={`flow-pane__layer flow-pane__layer--photo${
              isPhoto ? ' is-active' : ''
            }`}
            aria-hidden={!isPhoto}
          >
            <PhotoLeft />
          </div>
        </div>
      }
      right={
        <div className="flow-pane">
          <div
            className={`flow-pane__layer flow-pane__layer--form${
              !isPhoto ? ' is-active' : ''
            }`}
            aria-hidden={isPhoto}
          >
            <FormRight />
          </div>
          <div
            className={`flow-pane__layer flow-pane__layer--photo${
              isPhoto ? ' is-active' : ''
            }`}
            aria-hidden={!isPhoto}
          >
            <PhotoRight active={isPhoto} />
          </div>
        </div>
      }
    />
  );
}
